import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { Icon } from 'components/UI/Icons';
import { Colors, Svgs } from 'environment';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, FieldErrors, Resolver, useForm, useFormState, useWatch } from 'react-hook-form';
import {
	FormItem,
	GroupFormItem,
	isCategoryFixedFormItem,
	isCategoryMultipleFixedFormItem,
	isVariableFormItem,
	ProjectData,
	SeriesFormItem,
	VariableFormItem
} from './data/useProjectData/useProjectData';
import { CategoryMultipleVariableInput } from './inputs/CategoryMultipleNonFixedVariableInput';
import { CategoryVariableInput } from './inputs/CategoryNonFixedVariableInput';
import { DateTimeVariableInput } from './inputs/DateTimeVariableInput';
import { DateVariableInput } from './inputs/DateVariableInput';
import { FileVariableInput } from './inputs/FileVariableInput';
import { NumericVariableInput } from './inputs/NumericVariableInput';
import { StringVariableInput } from './inputs/StringVariableInput';
import { TimeDurationVariableInput } from './inputs/TimeDurationVariableInput';
import { UserDefinedUniqueVariableInput } from './inputs/UserDefinedUniqueInput';
import { Entry, Variable } from './types';
import { toZodSchema } from './utils/zodUtils';

import { CircleCheckIcon, SendIcon } from '@icons';
import { useTracking } from 'app/tracking/TrackingProvider';
import { useIsSurveyEnabledForProject } from 'features/entry-form-v2/survey/data/useIsSurveyEnabled';
import { useLanguage } from 'features/hooks/useLanguage';
import { usePrompt } from 'hooks/navigation/usePrompt';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LanguageType } from 'types/enums';
import { IconButton } from './component/IconButton';
import { ConfirmDiffModal } from './confirm-diff-modal/ConfirmDiffModal';
import { CategoryFixedVariableInput } from './inputs/CategoryFixedVariableInput';
import { CategoryMultipleFixedVariableInput } from './inputs/CategoryMultipleFixedVariableInput';
import {
	useVariableVisibilityFunction,
	VisibilityRuleTrigger
} from './inputs/DependencyVisibilityTrigger';
import { useProcessSeriesEntryBodyRows } from './series/details/SeriesDetailsPage';
import { isEmptyFormValue } from './smart-components/entry-history-modal/EntryHistoryModal';
import { PersonalDataWarning } from './smart-components/PersonalDataWarning';
import { SafariUserWarning } from './smart-components/SafariUserWarning';
import {
	SeriesEntryBody,
	TableSkeleton
} from './smart-components/series-entry-body/SeriesEntryBody';
import { AutoClearedFieldsInfo } from './smart-components/AutoClearedFieldsInfo';
import { useSeriesTablesDataQuery } from './smart-components/series-entry-body/useSeriesTablesDataQuery';
import { SeriesEntryCount } from './smart-components/SeriesEntryCount';
import { CreateSurveyInvitationModal } from './survey/components/CreateSurveyInvitationModal';
import { SurveyStatusModal } from './survey/components/SurveyStatusModal';
import { DiffedField, getDirtyFields, ParsedEntryValue } from './utils/formUtils';
import {
	defaultEmptyValueForVariables,
	parseBackendValues,
	ParsedEntry
} from './utils/parse-backend-values/parseBackendValues';
import { ROUTE_MAP } from './utils/routeMap';
import { AutoClearedFieldsContext, FieldEntry, Fields } from './AutoClearedFieldsContext';

const SUBMIT_ACTION_TYPES = [
	'save',
	'saveAndClose',
	'saveAndCreateNew',
	'saveAndNavigate'
] as const;
export type SubmitActionType = (typeof SUBMIT_ACTION_TYPES)[number];

type SubmitAction = {
	type: 'save' | 'saveAndClose' | 'saveAndCreateNew';
};
type SubmitActionWithNavigate = {
	type: 'saveAndNavigate';
	nextNavLocation: string;
};

export type NextAction = SubmitAction | SubmitActionWithNavigate;

interface Props {
	initialEntry?: Entry;
	onSubmitForConfirmation: (args: {
		nextAction: SubmitAction;
		dirtyData: ParsedEntry;
	}) => Promise<void>;
	onSubmitForSubmission: (args: {
		nextAction: SubmitActionWithNavigate;
		dirtyData: ParsedEntry;
	}) => Promise<void>;
	Header?: React.ReactNode;
	projectData: ProjectData;
	submitting?: boolean;
	disabled?: boolean;

	/**
	 * Use this to allow the user to leave the page without the diff modal, even if there are unsaved pending changes in the form.
	 */
	allowLeavePageOverrideRef?: RefObject<boolean>;

	onDirtyChange?: (isDirty: boolean) => void;
}

export const EntryForm = ({
	projectData,
	initialEntry,
	onSubmitForConfirmation,
	onSubmitForSubmission,
	Header,
	submitting,
	allowLeavePageOverrideRef,
	disabled,
	onDirtyChange
}: Props) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const overrideErrors = useRef<Record<string, string | undefined>>({});
	const [autoClearedFields, setAutoClearedFieldsState] = useState<Fields>(new Map());

	const setField = useCallback((key: string, value: FieldEntry) => {
		setAutoClearedFieldsState(prev => new Map(prev).set(key, value));
	}, []);

	const deleteField = useCallback((key: string) => {
		setAutoClearedFieldsState(prev => {
			const next = new Map(prev);
			next.delete(key);
			return next;
		});
	}, []);

	const { control, handleSubmit, setError, getValues, formState } = useForm<Entry>({
		mode: 'onBlur',
		resolver: combineResolvers(
			zodResolver(toZodSchema(projectData.variables)),
			overrideErrorsResolver(overrideErrors)
		),
		defaultValues: initialEntry
			? parseBackendValues({ variables: projectData.variables, entry: initialEntry })
			: defaultEmptyValueForVariables(projectData.variables)
	});

	const { defaultValues } = formState;

	const handleOverrideError = (name: string, error: string | undefined) => {
		overrideErrors.current[name] = error;
		setError(name, { message: error, type: 'custom' });
	};

	const validateAndSubmit = (nextAction?: NextAction) => {
		handleSubmit(
			data => {
				const dirtyFields = getDirtyFields(getValues(), defaultValues || {});

				if (dirtyFields.length === 0) {
					toast.warning('No changes to submit', {
						position: 'top-center'
					});
					return;
				}
				const dirtyData = Object.fromEntries(
					Object.entries(data).filter(([key]) => dirtyFields.includes(key))
				);

				if (nextAction?.type === 'saveAndNavigate') {
					return onSubmitForSubmission({
						nextAction: nextAction,
						dirtyData
					});
				}

				onSubmitForConfirmation({
					nextAction: nextAction || { type: 'save' },
					dirtyData
				});
			},
			_error => {
				toast.error(
					'Could not submit entry, ensure all required fields are filled, and satisfies validation rules.',
					{
						position: 'top-center'
					}
				);
			}
		)();
	};

	const anyObligatory = projectData.variables.some(v => v.obligatory);
	return (
		<AutoClearedFieldsContext.Provider
			value={{ fields: autoClearedFields, setField, deleteField }}
		>
			{onDirtyChange && (
				<DirtyStateReporter
					control={control}
					defaultValues={defaultValues || {}}
					onDirtyChange={onDirtyChange}
				/>
			)}

			<DiffModalNavigationIntercept
				control={control}
				variables={projectData.variables}
				allowLeavePageOverrideRef={allowLeavePageOverrideRef}
				onSaveConfirmed={nextNavLocation => {
					validateAndSubmit({ type: 'saveAndNavigate', nextNavLocation });
				}}
				isSubmitting={submitting}
			/>

			<AutoClearedFieldsInfo />

			<form
				className="relative grid grid-cols-2 gap-10 mb-52"
				data-testid="entry-form"
				onSubmit={e => {
					e.preventDefault();

					const action = (e.nativeEvent as SubmitEvent).submitter?.dataset.action;
					const submitType = parseSubmitType(action);
					let nextAction: NextAction | undefined = undefined;

					if (submitType && submitType !== 'saveAndNavigate') {
						nextAction = { type: submitType };
					}

					validateAndSubmit(nextAction);
				}}
			>
				{Header}

				<PersonalDataWarning />

				{projectData.formTitle && (
					<h1 className="text-2xl font-semibold whitespace-pre-line col-span-full">
						{projectData.formTitle}
					</h1>
				)}

				{projectData.formItems.map((item, index) => (
					<FormItemComponent
						key={index}
						item={item}
						control={control}
						onError={handleOverrideError}
						disabled={disabled}
					/>
				))}

				{anyObligatory && (
					<p className="mt-4 text-sm col-span-full">{dictionary.requiredFields}</p>
				)}
			</form>

			<SafariUserWarning />
		</AutoClearedFieldsContext.Provider>
	);
};

const DirtyStateReporter = ({
	control,
	defaultValues,
	onDirtyChange
}: {
	control: FormControl;
	defaultValues: Record<string, unknown>;
	onDirtyChange: (isDirty: boolean) => void;
}) => {
	const currentValues = useWatch({ control });
	const isDirty = getDirtyFields(currentValues, defaultValues).length > 0;

	useEffect(() => {
		onDirtyChange(isDirty);
	}, [isDirty, onDirtyChange]);

	return null;
};

const DiffModalNavigationIntercept = ({
	control,
	variables,
	allowLeavePageOverrideRef,
	onSaveConfirmed,
	isSubmitting
}: {
	control: FormControl;
	variables: Variable[];
	allowLeavePageOverrideRef?: RefObject<boolean>;
	onSaveConfirmed: (nextNavLocation: string) => void;
	isSubmitting?: boolean;
}) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const { track } = useTracking();
	const navigate = useNavigate();

	const currentLocation = useLocation();
	const formState = useFormState({ control });
	const currentFormValues = useWatch({ control });

	const allowNavigation = useRef(false);
	const [nextNavLocation, setNextNavLocation] = useState<string>();

	const dirtyFields = getDirtyFields(currentFormValues, formState.defaultValues || {});

	const isDirty = dirtyFields.length > 0;

	const [showModal, setShowModal] = useState(false);

	// This is needed to intercept navigation from react router
	usePrompt(nextLocation => {
		if (allowLeavePageOverrideRef?.current) {
			return true;
		}

		const sameLocation = currentLocation.pathname === nextLocation.pathname;

		const oldSearchParams = new URLSearchParams(currentLocation.search);
		const newSearchParams = new URLSearchParams(nextLocation.search);

		const formWasChanged = oldSearchParams.get('formId') !== newSearchParams.get('formId');

		if (sameLocation && !formWasChanged) {
			return true;
		}

		if (allowLeavePageOverrideRef?.current) {
			return true;
		}

		if (isDirty && !allowNavigation.current) {
			setShowModal(true);
			const nextLocationWithSearch = nextLocation.pathname + nextLocation.search;
			setNextNavLocation(nextLocationWithSearch);
			return false;
		}

		return true;
	}, isDirty);

	const diff: Record<string, DiffedField> = {};
	for (const field of dirtyFields) {
		diff[field] = {
			// the amount of boilerplate needed to not have to cast was a bit too much, this replaces any anyways, so it's a small improvement I guess
			from: formState.defaultValues?.[field] as ParsedEntryValue,
			to: currentFormValues[field] as ParsedEntryValue
		};
	}

	if (!showModal || !isDirty) return null;
	return (
		<ConfirmDiffModal
			title={dictionary.unsavedChanges}
			diff={diff}
			onClose={() => {
				setShowModal(false);
				track({
					eventName: 'entry_diff_cancelled'
				});
			}}
			submitting={isSubmitting}
			onConfirm={() => {
				track({
					eventName: 'entry_diff_confirmed'
				});

				if (!nextNavLocation) {
					console.error(
						new Error('nextNavLocation is not set, this is not supposed to happen')
					);
					return;
				}

				onSaveConfirmed(nextNavLocation);
			}}
			onDelete={() => {
				track({
					eventName: 'entry_diff_discarded'
				});
				if (!nextNavLocation) {
					console.error(
						new Error('nextNavLocation is not set, this is not supposed to happen')
					);
					return;
				}
				allowNavigation.current = true;
				navigate(nextNavLocation);
			}}
			variables={variables}
		/>
	);
};

const combineResolvers = (
	zodResolver: Resolver<ParsedEntry>,
	customResolver: Resolver<ParsedEntry>
): Resolver<ParsedEntry> => {
	return async (values: ParsedEntry, context, options) => {
		const [zodResults, customResults] = await Promise.all([
			zodResolver(values, context, options),
			customResolver(values, context, options)
		]);

		// TODO: MARTIN WRITE TESTS FOR THIS 🤘
		const hasErrors = !isEmptyObject(zodResults.errors) || !isEmptyObject(customResults.errors);

		return {
			values: hasErrors ? {} : zodResults.values, // zodResults.values is the values that passed the zod validation
			errors: {
				...zodResults.errors,
				...customResults.errors
			}
		};
	};
};

const isEmptyObject = (obj: Record<string, any>) => {
	return Object.keys(obj).length === 0 && obj.constructor === Object;
};

const overrideErrorsResolver = (
	ref: React.MutableRefObject<Record<string, string | undefined>>
): Resolver<ParsedEntry> => {
	return async (values: ParsedEntry, _, __) => {
		const errors: FieldErrors<ParsedEntry> = {};

		for (const [name, error] of Object.entries(ref.current)) {
			if (error !== undefined) {
				errors[name] = { type: 'custom', message: error };
			}
		}

		return { values, errors };
	};
};

const FormItemComponent = ({
	item,
	control,
	onError,
	disabled
}: {
	item: FormItem;
	control: FormControl;
	onError: (name: string, error: string | undefined) => void;
	disabled?: boolean;
}) => {
	switch (item.type) {
		case 'variable': {
			return (
				<VisibilityRuleTrigger control={control} variableFormItem={item}>
					<VariableInput
						control={control}
						item={item}
						onError={onError}
						disabled={disabled}
					/>
				</VisibilityRuleTrigger>
			);
		}

		case 'group': {
			return (
				<GroupContainer
					groupLabel={item.group.groupLabel}
					formItem={item}
					onError={onError}
					control={control}
					disabled={disabled}
				/>
			);
		}

		case 'separator':
			return <hr className="col-span-full" />;

		case 'subtitle':
			return (
				<h2 className="text-base font-semibold whitespace-pre-line col-span-full">
					{item.text}
				</h2>
			);

		case 'text':
			return <p className="text-base whitespace-pre-line col-span-full">{item.text}</p>;

		case 'series': {
			return <SeriesContainer formItem={item} disabled={disabled ?? true} />;
		}
	}
};

const SeriesContainer = ({
	formItem,
	disabled
}: {
	formItem: SeriesFormItem;
	disabled: boolean;
}) => {
	const params = useParams();
	const projectId = params.projectId as string;
	const entryId = params.entryId as string;

	if (entryId) {
		return (
			<UpdateEntrySeriesContainer
				formItem={formItem}
				projectId={projectId}
				entryId={entryId}
				disabled={disabled}
			/>
		);
	}

	return <CreateEntrySeriesContainer formItem={formItem} />;
};

const CreateEntrySeriesContainer = ({ formItem }: { formItem: SeriesFormItem }) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const [isOpen, setIsOpen] = useState(false);

	return (
		<div className="flex flex-col rounded-lg col-span-full shadow-normal ">
			<button
				type="button"
				className="flex items-center justify-between p-6"
				onClick={() => setIsOpen(!isOpen)}
			>
				<div className="flex items-center gap-1">
					<Icon
						svg={Svgs.ChevronDown}
						className={clsx('transition-all', isOpen && 'rotate-180')}
						propagate
					/>

					<Icon size={s => s.m} svg={Svgs.Set} />

					<h2 className="text-base font-semibold">{formItem.label}</h2>
				</div>
			</button>

			{isOpen && (
				<div className="px-6 pb-6">
					<p className="text-base text-gray-700">
						{dictionary.saveEntryToRegisterSeriesData}
					</p>
				</div>
			)}
		</div>
	);
};

const UpdateEntrySeriesContainer = ({
	formItem,
	projectId,
	entryId,
	disabled
}: {
	formItem: SeriesFormItem;
	projectId: string;
	entryId: string;
	disabled: boolean;
}) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const isSurveyFlagEnabled = useIsSurveyEnabledForProject();

	const [isOpen, setIsOpen] = useState(false);
	const [showSendSurveyModal, setShowSendSurveyModal] = useState(false);

	const showSurvey = isSurveyFlagEnabled && formItem.enableSurvey && !disabled;

	return (
		<div
			className="flex flex-col rounded-lg col-span-full shadow-normal"
			data-scroll-anchor
			id={`scroll-anchor_${formItem.name}`}
		>
			<button
				type="button"
				className="flex items-center justify-between p-6"
				onClick={() => setIsOpen(!isOpen)}
			>
				<div className="flex items-center gap-1">
					<Icon
						svg={Svgs.ChevronDown}
						className={clsx('transition-all', isOpen && 'rotate-180')}
						propagate
					/>

					<Icon size={s => s.m} svg={Svgs.Set} propagate />

					<h2 className="text-base font-semibold">{formItem.label}</h2>
					{showSurvey && (
						<>
							<IconButton
								onClick={e => {
									e.stopPropagation();
									setShowSendSurveyModal(true);
								}}
								className="px-2 py-1 ml-2 font-semibold rounded-md text-primary-700 bg-primary-500/10 hover:bg-primary-500/20"
							>
								<SendIcon className="size-5" />
								{dictionary.createSurvey}
							</IconButton>
							<SurveyStatusModal seriesName={formItem.name} />
						</>
					)}
				</div>

				<div className="flex items-center gap-4">
					<SeriesEntryCount seriesName={formItem.name} />

					<Link
						to={ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.createPath({
							projectId,
							entryId,
							seriesName: formItem.name
						})}
					>
						<Icon
							svg={Svgs.Maximize}
							size={s => s.m}
							colors={{
								color: Colors.primary.normal
							}}
							variant={v => v.button}
							propagate
						/>
					</Link>

					<Link
						className="border rounded-full border-primary-500"
						to={ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.create.createPath(
							{
								projectId,
								entryId,
								seriesName: formItem.name,
								formId: null
							}
						)}
					>
						<Icon
							svg={Svgs.Add}
							size={s => s.m}
							variant={v => v.button}
							colors={{
								color: Colors.primary.normal
							}}
							propagate
						/>
					</Link>
				</div>
			</button>

			{isOpen && (
				<div className="px-6 pb-6">
					<SeriesEntryBodyContainer
						entryId={entryId}
						projectId={projectId}
						seriesName={formItem.name}
					/>
				</div>
			)}

			<CreateSurveyInvitationModal
				open={showSendSurveyModal}
				seriesName={formItem.name}
				onClose={() => setShowSendSurveyModal(false)}
			/>
		</div>
	);
};

const SeriesEntryBodyContainer = ({
	entryId,
	projectId,
	seriesName
}: {
	entryId: string;
	projectId: string;
	seriesName: string;
}) => {
	const processedColumns = useSeriesTablesDataQuery({ projectId, entryId, seriesName });
	const processedRows = useProcessSeriesEntryBodyRows({
		entryId,
		projectId,
		seriesName
	});

	if (processedRows.loading) {
		return <TableSkeleton />;
	}

	if (processedRows.error) {
		return <p className="font-semibold text-error-500">Error fetching entries</p>;
	}

	if (!processedColumns.data) {
		return <p className="font-semibold text-error-500">Error processing columns</p>;
	}

	if (!processedRows.data) {
		return <p className="font-semibold text-error-500">Error processing rows</p>;
	}

	return (
		<SeriesEntryBody
			seriesName={seriesName}
			columns={processedColumns.data.columns}
			rows={processedRows.data}
			variables={processedColumns.data.variables}
			projectId={projectId}
			entryId={entryId}
		/>
	);
};

const GroupContainer = ({
	groupLabel,
	formItem,
	onError,
	control,
	disabled
}: {
	groupLabel: string;
	formItem: GroupFormItem;
	onError: (name: string, error: string | undefined) => void;
	control: FormControl;
	disabled?: boolean;
}) => {
	const variableFormItems: VariableFormItem[] = useMemo(
		() => formItem.formItems.filter(isVariableFormItem),
		[formItem]
	);

	const [isOpen, setIsOpen] = useState(
		formItem.formItems.some(
			formItem => isVariableFormItem(formItem) && formItem.variable.obligatory
		)
	);

	return (
		<div
			className="relative flex flex-col p-6 rounded-lg col-span-full shadow-normal"
			data-scroll-anchor
			id={`scroll-anchor_${formItem.group.groupName}`}
		>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center justify-between"
				type="button"
			>
				<div className="flex items-center self-stretch gap-2">
					<Icon
						onClick={() => setIsOpen(!isOpen)}
						svg={Svgs.ChevronDown}
						className={clsx('transition-all', isOpen && 'rotate-180')}
					/>

					<Icon size={s => s.s} svg={Svgs.Folder} />

					<h2 className="text-base font-semibold">{groupLabel}</h2>
				</div>

				<GroupStatusIcon control={control} variableFormItems={variableFormItems} />
			</button>

			<div
				className={clsx(
					'grid grid-cols-2 py-[10px] gap-10 lg:gap-y-10',
					!isOpen && 'hidden'
				)}
			>
				{formItem.formItems.map((item, index) => (
					<FormItemComponent
						key={index}
						item={item}
						control={control}
						onError={onError}
						disabled={disabled}
					/>
				))}
			</div>
		</div>
	);
};

const GroupStatusIcon = ({
	control,
	variableFormItems
}: {
	control: FormControl;
	variableFormItems: VariableFormItem[];
}) => {
	const formState = useFormState({ control });

	const errors = formState.errors;

	const hasErrors = variableFormItems.some(v => !!errors[v.variable.variableName]);

	const allValuesInGroup = useWatch({
		control,
		name: variableFormItems.map(v => v.variable.variableName)
	});

	const { isVariableVisible } = useVariableVisibilityFunction({ control, variableFormItems });

	const allVariablesValidated = useMemo(() => {
		let allValid = true;

		for (let i = 0; i < variableFormItems.length; i++) {
			const variableFormItem = variableFormItems[i];
			const isVisible = isVariableVisible(variableFormItem);
			if (!isVisible) {
				continue;
			}
			const value = allValuesInGroup[i];
			if (isEmptyFormValue(value)) {
				allValid = false;
			}
		}
		return allValid;
	}, [allValuesInGroup, isVariableVisible, variableFormItems]);

	if (hasErrors) {
		return (
			<Icon svg={Svgs.AlertCircle} colors={{ color: Colors.text.error }} size={s => s.m} />
		);
	}

	if (allVariablesValidated) {
		return (
			<CircleCheckIcon
				className="text-[16px] text-success-500"
				aria-label="All variables are valid"
			/>
		);
	}

	return null;
};

export type FormControl = Control<Entry, any>;

const VariableInput = ({
	item,
	control,
	onError,
	disabled
}: {
	item: VariableFormItem;
	control: FormControl;
	onError: (name: string, error: string | undefined) => void;
	disabled?: boolean;
}) => {
	switch (item.variableType) {
		case 'string':
			return <StringVariableInput control={control} formItem={item} disabled={disabled} />;

		case 'float':
		case 'integer': {
			return <NumericVariableInput control={control} formItem={item} disabled={disabled} />;
		}

		case 'category': {
			if (isCategoryFixedFormItem(item)) {
				return (
					<CategoryFixedVariableInput control={control} item={item} disabled={disabled} />
				);
			}
			return <CategoryVariableInput control={control} item={item} disabled={disabled} />;
		}

		case 'categoryMultiple': {
			if (isCategoryMultipleFixedFormItem(item)) {
				return (
					<CategoryMultipleFixedVariableInput
						control={control}
						item={item}
						disabled={disabled}
					/>
				);
			}
			return (
				<CategoryMultipleVariableInput control={control} item={item} disabled={disabled} />
			);
		}

		case 'date': {
			return <DateVariableInput control={control} formItem={item} disabled={disabled} />;
		}

		case 'datetime': {
			return <DateTimeVariableInput control={control} formItem={item} disabled={disabled} />;
		}

		case 'file': {
			return (
				<FileVariableInput
					onError={onError}
					control={control}
					formItem={item}
					disabled={disabled}
				/>
			);
		}

		case 'userDefinedUnique': {
			return (
				<UserDefinedUniqueVariableInput
					control={control}
					formItem={item}
					disabled={disabled}
				/>
			);
		}

		case 'timeDuration': {
			return (
				<TimeDurationVariableInput
					control={control}
					formItem={item}
					onError={onError}
					disabled={disabled}
				/>
			);
		}
	}

	console.error(new Error('Unhandled variable type: '), {
		// @ts-ignore
		variableType: variable.variableType
	});

	return null;
};

const parseSubmitType = (type: string | undefined): SubmitActionType | undefined => {
	if (!type) return undefined;

	if (SUBMIT_ACTION_TYPES.some(t => t === type)) {
		return type as SubmitActionType;
	}

	console.warn(`Invalid submit type: ${type}`);
	return undefined;
};

export const createSubmitEvent = (submitter: HTMLElement): Event => {
	let submitEvent: Event;

	try {
		submitEvent = new SubmitEvent('submit', {
			bubbles: true,
			cancelable: true,
			submitter: submitter
		});
	} catch (e) {
		submitEvent = new Event('submit', {
			bubbles: true,
			cancelable: true
		});

		// Extend the event with submitter property
		Object.assign(submitEvent, { submitter });
	}

	return submitEvent;
};

const DICTIONARY = {
	[LanguageType.English]: {
		requiredFields: 'Fields marked with an (*) are required',
		unsavedChanges: 'You have unsaved changes',
		createSurvey: 'Create survey',
		saveEntryToRegisterSeriesData: 'Save the entry to be able to register series data'
	},
	[LanguageType.Norwegian]: {
		requiredFields: 'Felt merket med (*) er obligatoriske',
		unsavedChanges: 'Du har ulagrede endringer',
		createSurvey: 'Lag undersøkelse',
		saveEntryToRegisterSeriesData: 'Lagre oppføringen for å kunne registrere serie data'
	}
};
